// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { Icon } from '@iconify/react'

// ** Custom Components Imports
import { equipmentList } from 'src/data/equipment'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import { nFormatter } from 'src/@core/utils/numberFormatter'

/*
  Igual que la lista de artefactos: la pintaba un DataGrid (virtualizado, cero
  filas en el HTML del servidor) dentro de una pestaña que no era la activa por
  defecto. Las 78 piezas de equipo no aparecian en out/equipment/index.html — 0 de
  8 nombres buscados.

  Tres cosas mas que salieron al reescribirlo:

   - `getData` se llamaba en cada render y otra vez completo en cada tecla del
     filtro. Es una funcion pura sobre un array constante, asi que ahora se
     resuelve una sola vez al cargar el modulo.
   - La logica de busqueda estaba duplicada con la de las columnas, calculando lo
     mismo dos veces con formatos distintos.
   - El encabezado decia 'ARTIFACTS LIST' en la pagina de equipo. Copy-paste.
*/

/**
 * Aplana cada pieza a una fila de tabla: materiales y stats por calidad quedan
 * como texto de varias lineas, que es como los muestra la celda y como los busca
 * el filtro.
 */
const aplanar = lista =>
  lista.map(b => {
    const stats = calidad =>
      (b.levels.find(l => l.quality === calidad)?.stats ?? [])
        .map(s => `${s.statDesc} ${Math.ceil(s.stat) > 999 ? nFormatter(s.stat, 1) : `${s.stat}%`}`)
        .join('\n')

    return {
      type: b.type,
      equipment: b.desc,
      level: b.level,
      recommended: b.troop,
      materials: b.materials.map(m => `${m.m} x${m.q}`).join('\n'),
      statsPurple: stats('Purple'),
      statsOrange: stats('Orange')
    }
  })

const filas = aplanar(equipmentList)

/** Celda de varias lineas: un <span> por linea, para que no se pierda el salto. */
const multilinea = v => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    {(v ?? '')
      .split('\n')
      .filter(Boolean)
      .map((linea, i) => (
        <Typography key={`${linea}-${i}`} variant='body2'>
          {linea}
        </Typography>
      ))}
  </Box>
)

const columnas = [
  { clave: 'type', encabezado: 'Type', ordenable: true, ancho: 90 },
  { clave: 'equipment', encabezado: 'Equipment', ordenable: true },
  { clave: 'level', encabezado: 'Level', numerica: true, ordenable: true, ancho: 70 },
  { clave: 'recommended', encabezado: 'Recommended For', ordenable: true, ancho: 130 },
  { clave: 'materials', encabezado: 'Materials', formato: multilinea },
  { clave: 'statsPurple', encabezado: 'Stats Purple', formato: multilinea },
  { clave: 'statsOrange', encabezado: 'Stats Orange', formato: multilinea }
]

const EquipmentList = ({ tabConf }) => {
  return (
    <Card>
      <Box
        sx={{
          marginBottom: '10px',
          marginTop: '10px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center'
        }}
      >
        <Typography variant='h5' component='p' color='primary' align='center'>
          <Icon fontSize={30} icon={tabConf['list'].icon} />
        </Typography>
        <Typography variant='h5' component='p' color='primary' align='center' sx={{ marginLeft: '5px' }}>
          {' EQUIPMENT LIST'}
        </Typography>
      </Box>
      <Divider />
      <TablaReferencia
        columnas={columnas}
        filas={filas}
        idFila='equipment'
        titulo='War and Order equipment: materials and stat bonuses by quality'
        clavesFiltro={['equipment', 'type', 'recommended', 'materials', 'statsPurple', 'statsOrange']}
      />
    </Card>
  )
}

export default EquipmentList
